import { useState, useRef, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Mail, ArrowLeft, KeyRound } from 'lucide-react';
import { redirectByRole } from 'contexts/AuthContext';
const LoginByCode = () => {
  const { setAuth } = useAuth();

  const navigate = useNavigate();

  const [step, setStep]             = useState('code');
  const [accessCode, setAccessCode] = useState('');
  const [otp, setOtp]               = useState(['', '', '', '', '', '']);
  const [loading, setLoading]       = useState(false);
  const [emailHint, setEmailHint]   = useState('');
  const [userId, setUserId]         = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const otpRefs = useRef([]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // ── PASO 1: enviar código de acceso ──────────────────────────────────
  const handleSubmitCode = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login-by-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: accessCode.trim() }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.detail || 'Error al iniciar sesión');

      if (data.access_token) {
        setAuth(data.access_token, data.user);
        navigate(redirectByRole(data.user));
        return;
      }

      if (data.requires_otp) {
        setEmailHint(data.email_hint);
        setUserId(data.user_id);
        setStep('otp');
        setResendCooldown(60);
        toast.success(data.message);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Función core de verificación (acepta el código como parámetro) ───
  const handleVerifyOtpWithCode = async (otpStr) => {
    if (!otpStr || otpStr.length < 6) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: accessCode.trim(), otp: otpStr }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Código incorrecto');
      login(data.access_token, data.user);
    } catch (err) {
      toast.error(err.message);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // ── PASO 2: verificar OTP (botón manual) ─────────────────────────────
  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    handleVerifyOtpWithCode(otp.join(''));
  };

  // ── Reenviar OTP ──────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login-by-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: accessCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Nuevo código enviado');
      setResendCooldown(60);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Manejo de inputs OTP ──────────────────────────────────────────────
  const handleOtpChange = (idx, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[idx] = val.slice(-1);
    setOtp(next);
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus();
    // Auto-submit con el valor actualizado directamente
    if (next.join('').length === 6) {
      setTimeout(() => handleVerifyOtpWithCode(next.join('')), 80);
    }
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replaceAll(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
      // Auto-submit con el valor pegado directamente
      setTimeout(() => handleVerifyOtpWithCode(pasted), 80);
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 px-8 py-8 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {step === 'code'
              ? <KeyRound className="w-8 h-8 text-white" />
              : <ShieldCheck className="w-8 h-8 text-white" />
            }
          </div>
          <h1 className="text-2xl font-bold">
            {step === 'code' ? 'Acceder a la plataforma' : 'Verificación en dos pasos'}
          </h1>
          <p className="text-white/80 text-sm mt-1">
            {step === 'code'
              ? 'Introduce tu código de acceso'
              : `Código enviado a ${emailHint}`
            }
          </p>
        </div>

        <div className="px-8 py-8">

          {/* ── PASO 1: código de acceso ── */}
          {step === 'code' && (
            <form onSubmit={handleSubmitCode} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Código de acceso
                </label>
                <input
                  type="text"
                  value={accessCode}
                  onChange={e => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX"
                  maxLength={9}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center text-xl font-mono tracking-widest focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={loading || accessCode.length < 8}
                className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              >
                {loading
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : <KeyRound className="w-5 h-5" />
                }
                {loading ? 'Verificando…' : 'Continuar'}
              </button>
            </form>
          )}

          {/* ── PASO 2: OTP ── */}
          {step === 'otp' && (
            <div className="space-y-6">

              {/* Info email */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Revisa tu email</p>
                  <p className="text-xs text-blue-600 mt-0.5">
                    Enviamos un código de 6 dígitos a <strong>{emailHint}</strong>.<br/>
                    Caduca en 10 minutos.
                  </p>
                </div>
              </div>

              {/* Inputs OTP */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                  Introduce el código
                </label>
                <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={el => otpRefs.current[idx] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(idx, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(idx, e)}
                      className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl outline-none transition-all
                        ${digit
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 focus:border-green-500'
                        }
                        ${loading ? 'opacity-50 pointer-events-none' : ''}
                      `}
                      autoFocus={idx === 0}
                    />
                  ))}
                </div>
              </div>

              {/* Botón verificar manual */}
              <button
                onClick={handleVerifyOtp}
                disabled={loading || otp.join('').length < 6}
                className="w-full py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              >
                {loading
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : <ShieldCheck className="w-5 h-5" />
                }
                {loading ? 'Verificando…' : 'Verificar código'}
              </button>

              {/* Reenviar + volver */}
              <div className="flex items-center justify-between text-sm">
                <button
                  onClick={() => {
                    setStep('code');
                    setOtp(['', '', '', '', '', '']);
                  }}
                  className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeft className="w-4 h-4" /> Volver
                </button>
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || loading}
                  className="text-green-600 hover:text-green-700 disabled:text-gray-400 disabled:cursor-not-allowed font-medium"
                >
                  {resendCooldown > 0
                    ? `Reenviar (${resendCooldown}s)`
                    : 'Reenviar código'
                  }
                </button>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginByCode;